import React, { useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { SkeletonLoaderReviewCreation } from 'screens/admin/Reviews/ReviewProcessV1/components/SkeletonLoaderReviewCreation';

import { Paper } from 'uiToolkitV1/Paper';
import { TextBodyLarge, TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

export interface ExpandablePaperProps {
  children:
    | (({
        isPanelOpen,
        setIsPanelOpen,
      }: {
        isPanelOpen?: boolean;
        setIsPanelOpen?: React.Dispatch<React.SetStateAction<boolean>>;
      }) => React.ReactNode)
    | React.ReactNode;
  boxShadow?: boolean | string;
  border?: boolean | string;
  isAccordion?: boolean;
  [key: string]: any;
}

export interface ExpandablePaperHeaderProps {
  title: React.ReactNode;
  subTitle?: string;
  leftIconComponent?: React.ReactNode;
  isPanelOpen?: boolean;
  setIsPanelOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  rightComponent?: React.ReactNode;
  [key: string]: any;
}

export interface ExpandablePaperBodyProps {
  children: React.ReactNode;
  isPanelOpen?: boolean;
  [key: string]: any;
}

export const ExpandablePaper = ({
  children,
  boxShadow,
  border,
  isAccordion,
  showLoader = false,
  ...props
}: ExpandablePaperProps) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const finalShadow = boxShadow ?? '0px 0px 2px 0px rgba(0, 0, 0, .24)';
  const finalBorder = border === true ? '1px solid #EBEFF3' : border;

  const propsForChildren = {
    isPanelOpen: isAccordion ? isPanelOpen : true,
    setIsPanelOpen: isAccordion ? setIsPanelOpen : undefined,
  };

  const comp = typeof children === 'function' ? children(propsForChildren) : children;

  if (showLoader) {
    return (
      <Paper p="12px 16px 16px 16px" boxShadow={finalShadow} border={finalBorder} w="100%" {...props}>
        <SkeletonLoaderReviewCreation />
      </Paper>
    );
  }

  return (
    <Paper p="12px 16px 16px 16px" boxShadow={finalShadow} border={finalBorder} w="100%" {...props}>
      {comp}
    </Paper>
  );
};

export const ExpandablePaperHeader = ({
  leftIconComponent,
  title,
  subTitle,
  isPanelOpen,
  setIsPanelOpen,
  rightComponent = null,
  ...props
}: ExpandablePaperHeaderProps) => (
  <Flex
    borderBottom={isPanelOpen === false ? '' : '1px solid #CFD7DF'}
    mb={2}
    pb={3}
    alignItems="center"
    onClick={() => setIsPanelOpen?.((isPanelOpen) => !isPanelOpen)}
    cursor={setIsPanelOpen ? 'pointer' : 'initial'}
    justifyContent="space-between"
    {...props}
  >
    <Flex alignItems="center" p={2}>
      {leftIconComponent}
      <Flex flexDirection="column" ml={2}>
        <TextBodyLarge bold>{title}</TextBodyLarge>
        <TextLabel color="text.g2">{subTitle}</TextLabel>
      </Flex>
    </Flex>
    <Flex>{rightComponent}</Flex>
  </Flex>
);

export const ExpandablePaperHeader1 = ({ title, subTitle, rightComponent = null }: ExpandablePaperHeaderProps) => (
  <Flex pb={3} justifyContent="space-between" gap={6}>
    <Flex flexDirection="column" maxW="500px">
      <TextBodyMedium bold>{title}</TextBodyMedium>
      <TextLabel color="text.g2">{subTitle}</TextLabel>
    </Flex>

    <Flex justifyContent="flex-end" flexGrow={1} flexShrink={0}>
      {rightComponent}
    </Flex>
  </Flex>
);

export const ExpandablePaperBody = ({ children, isPanelOpen, ...props }: ExpandablePaperBodyProps) => {
  if (isPanelOpen === false) {
    return <></>;
  }

  return (
    <Flex flexDir="column" {...props}>
      {children}
    </Flex>
  );
};
